import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Descriptions, Table, Card, Typography, message, Image, Button, Modal, Select, Popconfirm, Grid } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../logo.png";
import api from "../api";
import EditIngresoCajaModal from "../components/EditIngresoCajaModal";
import EditIngresoGranelModal from "../components/EditIngresoGranelModal";
import EditEgresoModal from "../components/EditEgresoModal";
import EditProcesoModal from "../components/EditProcesoModal";

const { Title } = Typography;
const { useBreakpoint } = Grid;

const DetalleProcesoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [proceso, setProceso] = useState(null);
  const [ingresosGranel, setIngresosGranel] = useState([]);
  const [ingresosCajas, setIngresosCajas] = useState([]);
  const [egresos, setEgresos] = useState([]);
  const [imagenes, setImagenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);

  // Edit Modals State
  const [editIngresoCajaVisible, setEditIngresoCajaVisible] = useState(false);
  const [selectedIngresoCaja, setSelectedIngresoCaja] = useState(null);
  const [editIngresoGranelVisible, setEditIngresoGranelVisible] = useState(false);
  const [selectedIngresoGranel, setSelectedIngresoGranel] = useState(null);
  const [editEgresoVisible, setEditEgresoVisible] = useState(false);
  const [selectedEgreso, setSelectedEgreso] = useState(null);
  const [editProcesoVisible, setEditProcesoVisible] = useState(false);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewPdfUri, setPreviewPdfUri] = useState("");
  const [previewPdfBase64, setPreviewPdfBase64] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewFilename, setPreviewFilename] = useState("");
  const [previewReportName, setPreviewReportName] = useState("");
  const [sendingPdf, setSendingPdf] = useState(false);

  const userRole = localStorage.getItem("rol") || "Operador";

  const fetchProceso = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/proceso/${id}`);
      const data = res.data;

      setProceso({
        ...data,
      });
      setIngresosGranel(data.ingresosGranel || []);
      setIngresosCajas(data.ingresosCajas || []);
      setEgresos(data.egresos || []);

      // Fetch images
      try {
        const resImg = await api.get(`/proceso/imagenes/${id}`);
        if (resImg.data && resImg.data.data) {
          setImagenes(resImg.data.data);
        }
      } catch (imgError) {
        console.error("Error loading images", imgError);
      }

    } catch (err) {
      console.error(err);
      message.error("Error al cargar el proceso");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setNewStatus(proceso?.estado || "");
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setNewStatus("");
  };

  const handleDeleteImage = async (idImagen) => {
    try {
      await api.delete(`/proceso/imagen/${idImagen}`);
      message.success("Imagen eliminada exitosamente");
      setImagenes(prev => prev.filter(img => img.idImagen !== idImagen));
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        message.error(err.response.data.message);
      } else {
        message.error("Error al eliminar la imagen");
      }
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      message.warning("Por favor seleccione un estado");
      return;
    }

    setUpdatingStatus(true);
    try {
      if (newStatus === "Finalizado") {
        await api.put(`/proceso/finalizar/${id}`);
        message.success("Proceso finalizado e inventario actualizado");
      } else {
        await api.put(`/proceso/${id}/estado`, { estado: newStatus });
        message.success("Estado actualizado exitosamente");
      }
      setIsModalVisible(false);
      fetchProceso(); // Refresh process data
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al actualizar el estado";
      message.error(errorMsg);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteIngresoGranel = async (idingresos) => {
    try {
      await api.delete(`/ingresosGranel/eliminar/${idingresos}`);
      message.success("Ingreso eliminado exitosamente");
      fetchProceso();
    } catch (err) {
      message.error("Error al eliminar el ingreso");
    }
  };

  const handleDeleteIngresoCaja = async (idingresosCajas) => {
    try {
      await api.delete(`/ingresosCajas/eliminar/${idingresosCajas}`);
      message.success("Ingreso eliminado exitosamente");
      fetchProceso();
    } catch (err) {
      message.error("Error al eliminar el ingreso");
    }
  };

  const handleDeleteEgreso = async (idegresos) => {
    try {
      await api.delete(`/egresos/eliminar/${idegresos}`);
      message.success("Egreso eliminado exitosamente");
      fetchProceso();
    } catch (err) {
      message.error("Error al eliminar el egreso");
    }
  };

  useEffect(() => {
    fetchProceso();
  }, [id]);

  if (loading) return <p>Cargando...</p>;
  if (!proceso) return <p>No se encontró el proceso.</p>;

  // Columns for Ingresos Granel
  const columnsIngresosGranel = [
    { title: "Tara Individual (kg)", dataIndex: "taraIndividual" },
    { title: "Procesadas", dataIndex: "procesadas" },
    { title: "Peso Bruto Total (kg)", dataIndex: "pesoBrutoTotal" },
    {
      title: "Tara (kg)",
      render: (_, record) => (record.taraIndividual * record.procesadas).toFixed(2)
    },
    {
      title: "Peso Ingreso (kg)",
      render: (_, record) => (record.pesoBrutoTotal - (record.taraIndividual * record.procesadas)).toFixed(2)
    },
    { title: "Observaciones", dataIndex: "observaciones" },
    {
      title: "Acciones",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="link"
            onClick={() => {
              setSelectedIngresoGranel(record);
              setEditIngresoGranelVisible(true);
            }}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este registro?"
            onConfirm={() => handleDeleteIngresoGranel(record.idingresos)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Columns for Ingresos Cajas
  const columnsIngresosCajas = [
    { title: "Peso Neto (kg)", dataIndex: "pesoNeto" },
    { title: "Procesadas", dataIndex: "procesadas" },
    { title: "Descarte", dataIndex: "descarte" },
    {
      title: "Recibidas",
      render: (_, record) => (record.procesadas - (record.descarte || 0))
    },
    {
      title: "Peso Ingreso (kg)",
      render: (_, record) => {
        const recibidas = record.procesadas - (record.descarte || 0);
        return (record.pesoNeto * recibidas).toFixed(2);
      }
    },
    { title: "Observaciones", dataIndex: "observaciones" },
    {
      title: "Acciones",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="link"
            onClick={() => {
              setSelectedIngresoCaja(record);
              setEditIngresoCajaVisible(true);
            }}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este registro?"
            onConfirm={() => handleDeleteIngresoCaja(record.idingresosCajas)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Columns for Egresos
  const columnsEgresos = [
    { title: "Calibre", dataIndex: "calibre" },
    { title: "Kg por Caja", dataIndex: "kgCaja" },
    { title: "Cajas", dataIndex: "cajas" },
    {
      title: "Total Kg",
      render: (_, record) => (record.kgCaja * record.cajas).toFixed(2)
    },
    {
      title: "Acciones",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="link"
            onClick={() => {
              setSelectedEgreso(record);
              setEditEgresoVisible(true);
            }}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este registro?"
            onConfirm={() => handleDeleteEgreso(record.idegresos)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Calculos de totales
  const totalPesoBrutoGranel = ingresosGranel.reduce((sum, item) => sum + (item.pesoBrutoTotal || 0), 0);
  const totalProcesadasGranel = ingresosGranel.reduce((sum, item) => sum + (item.procesadas || 0), 0);
  const totalTaraGranel = ingresosGranel.reduce((sum, item) => sum + ((item.taraIndividual * item.procesadas) || 0), 0);
  const totalPesoIngresoGranel = totalPesoBrutoGranel - totalTaraGranel;

  // Calculos totales cajas
  const totalPesoNetoCajas = ingresosCajas.reduce((sum, item) => sum + (item.pesoNeto || 0), 0);
  const totalProcesadasCajas = ingresosCajas.reduce((sum, item) => sum + (item.procesadas || 0), 0);
  const totalDescarteCajas = ingresosCajas.reduce((sum, item) => sum + (item.descarte || 0), 0);
  const totalRecibidasCajas = ingresosCajas.reduce((sum, item) => sum + (item.procesadas - (item.descarte || 0)), 0);
  const totalPesoIngresoCajas = ingresosCajas.reduce((sum, item) => {
    const recibidas = item.procesadas - (item.descarte || 0);
    return sum + (item.pesoNeto * recibidas);
  }, 0);

  const totalCajasEgresos = egresos.reduce((sum, item) => sum + (item.cajas || 0), 0);
  const totalKgEgresos = egresos.reduce((sum, item) => sum + ((item.kgCaja * item.cajas) || 0), 0);

  const getWeekNumber = (d) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    var weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  const exportarInforme = () => {
    const doc = new jsPDF();

    // Helper to print label and value
    const printRow = (label, value, xLabel, xValue, y, maxWidth = 55) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, xLabel, y);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(String(value || ''), maxWidth);
      doc.text(lines, xValue, y);
      return lines.length;
    };

    // Header
    doc.addImage(logo, "PNG", 14, 14, 30, 6);
    doc.setFontSize(10);
    doc.text("INTERPACK S.A.S.", 50, 15);
    doc.setFontSize(8);
    doc.text(`Fecha de Generación: ${new Date().toLocaleString()}`, 130, 15);
    doc.text("Dirección: Santa Lucía - Ecuador", 50, 20);
    doc.text("Teléfono: 0980169890", 50, 25);
    doc.setFontSize(6);
    doc.text("Este comprobante no posee efectos tributarios y tiene como objetivo", 130, 20)
    doc.text("ser un respaldo exclusivamente informativo.", 130, 22);

    doc.line(14, 30, 196, 30);

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("DETALLE DE PROCESO DEL PRODUCTO #" + proceso.secuencial, 105, 40, { align: "center" });
    doc.setFont("helvetica", "normal");

    let yPos = 55;
    doc.setFontSize(10);

    const parts = proceso.fechaInicio ? proceso.fechaInicio.split(' ') : [];
    let fechaProc = new Date();
    if (parts.length > 0) {
      const dateParts = parts[0].split('/');
      const timeParts = parts.length > 1 ? parts[1].split(':') : [0, 0, 0];
      if (dateParts.length === 3) {
        fechaProc = new Date(
          parseInt(dateParts[2], 10),
          parseInt(dateParts[1], 10) - 1,
          parseInt(dateParts[0], 10),
          parseInt(timeParts[0] || 0, 10),
          parseInt(timeParts[1] || 0, 10),
          parseInt(timeParts[2] || 0, 10)
        );
      }
    }
    const semana = isNaN(fechaProc) ? "-" : getWeekNumber(fechaProc);

    const gavetasIngresadas = ingresosGranel.length > 0 ? totalProcesadasGranel : totalProcesadasCajas;
    const pesoBruto = ingresosGranel.length > 0 ? totalPesoBrutoGranel : totalPesoNetoCajas;
    const tara = ingresosGranel.length > 0 ? totalTaraGranel : 0;
    const pesoIngreso = ingresosGranel.length > 0 ? totalPesoIngresoGranel : totalPesoIngresoCajas;

    // Info Block
    let l1 = printRow("Proveedor:", proceso.productorNombre, 14, 50, yPos);
    let l2 = printRow("RUC:", proceso.productorIdentificacion, 110, 145, yPos);
    yPos += Math.max(l1, l2) * 5 + 2;
    l1 = printRow("Producto:", proceso.producto, 14, 50, yPos);
    l2 = printRow("Fecha de Proceso:", proceso.fechaInicio, 110, 145, yPos);
    yPos += Math.max(l1, l2) * 5 + 2;
    l1 = printRow("Semana de Proceso:", semana, 14, 50, yPos);
    l2 = printRow("Gavetas/Cajas:", gavetasIngresadas, 110, 145, yPos);
    yPos += Math.max(l1, l2) * 5 + 2;
    l1 = printRow("Peso Bruto (kg):", pesoBruto.toFixed(2), 14, 50, yPos);
    l2 = printRow("Tara (kg):", tara.toFixed(2), 110, 145, yPos);
    yPos += Math.max(l1, l2) * 5 + 2;
    l1 = printRow("Peso Ingreso (kg):", pesoIngreso.toFixed(2), 14, 50, yPos);
    l2 = printRow("Jornada:", proceso.jornada, 110, 145, yPos);
    yPos += Math.max(l1, l2) * 5 + 10;

    // Table 1: Egresos
    doc.setFont("helvetica", "bold");
    doc.text("DESGLOSE DE CALIBRES PROCESADOS", 14, yPos);
    doc.setFont("helvetica", "normal");
    yPos += 5;

    const columnsEgresosPDF = [
      { header: "Calibre", dataKey: "calibre" },
      { header: "Kg por Caja", dataKey: "kgCaja" },
      { header: "Cajas", dataKey: "cajas" },
      { header: "Total Kg", dataKey: "totalKg" }
    ];

    const dataEgresosPDF = egresos.map(item => ({
      calibre: item.calibre,
      kgCaja: item.kgCaja,
      cajas: item.cajas,
      totalKg: (item.kgCaja * item.cajas).toFixed(2)
    }));

    // Egresos Footer
    const footerEgresos = [{
      calibre: "TOTAL",
      kgCaja: "",
      cajas: totalCajasEgresos,
      totalKg: totalKgEgresos.toFixed(2)
    }];

    autoTable(doc, {
      head: [columnsEgresosPDF.map(c => c.header)],
      body: dataEgresosPDF.map(r => Object.values(r)),
      foot: footerEgresos.map(r => Object.values(r)),
      startY: yPos,
      theme: 'grid',
      styles: {
        fontSize: 8,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineColor: [0, 0, 0],
        lineWidth: 0.1
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineColor: [0, 0, 0],
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    doc.setFont("helvetica", "bold");
    // Table 2: Resumen Transposed
    doc.text("RESUMEN DEL PROCESO", 14, yPos);
    doc.setFont("helvetica", "normal");
    yPos += 5;

    const kgIngresados = (totalPesoIngresoGranel + totalPesoIngresoCajas).toFixed(2);
    const kgRechazo1 = (proceso.kgRechazo || 0).toFixed(2);
    const kgRechazo2 = ((totalPesoIngresoGranel + totalPesoIngresoCajas) - (proceso.kgRechazo || 0) - totalKgEgresos).toFixed(2);
    const totalKgRechazados = (((proceso.kgRechazo || 0)) + ((totalPesoIngresoGranel + totalPesoIngresoCajas) - (proceso.kgRechazo || 0) - totalKgEgresos)).toFixed(2);
    const porcentajeRechazo = (totalKgRechazados / kgIngresados * 100).toFixed(2);
    const totalKgEgresadosVal = totalKgEgresos.toFixed(2);

    const columnsResumen = [
      { header: "Concepto", dataKey: "concepto" },
      { header: "Peso Kg", dataKey: "peso" }
    ];

    const dataResumen = [
      { concepto: "Kg Ingresados", peso: kgIngresados },
      { concepto: "Kg Rechazo 1", peso: kgRechazo1 },
      { concepto: "Kg Rechazo 2", peso: kgRechazo2 },
      { concepto: "Total Kg Rechazados", peso: totalKgRechazados },
      { concepto: "% Rechazo", peso: porcentajeRechazo },
      { concepto: "Total Kg Procesados", peso: totalKgEgresadosVal }
    ];

    autoTable(doc, {
      head: [columnsResumen.map(c => c.header)],
      body: dataResumen.map(r => Object.values(r)),
      startY: yPos,
      theme: 'grid',
      styles: {
        fontSize: 8,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineColor: [0, 0, 0],
        lineWidth: 0.1
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },
      columnStyles: {
        1: { halign: 'center' }
      },
      didParseCell: function (data) {
        if (data.column.index === 1) {
          data.cell.styles.halign = 'center';
        }
        if (data.section === 'body' && data.row.raw[0] === "Total Kg Procesados") {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Signatures
    const finalY = (doc.lastAutoTable?.finalY || yPos) + 40;
    doc.line(30, finalY, 90, finalY);
    doc.text("Entregue Conforme", 40, finalY + 5);

    doc.line(120, finalY, 180, finalY);
    doc.text("Recibí Conforme", 135, finalY + 5);

    const uri = doc.output('datauristring');
    const base64 = uri.split('base64,')[1];
    setPreviewPdfUri(uri);
    setPreviewPdfBase64(base64);
    setPreviewTitle("Vista Previa del Informe de Proceso");
    setPreviewFilename(`Informe_Proceso_${proceso.secuencial}.pdf`);
    setPreviewReportName("Informe de Proceso");
    setPreviewModalVisible(true);
  };

  const exportarComprobante = () => {
    const doc = new jsPDF();
    const isGranel = ingresosGranel.length > 0;

    // Helper to print label and value
    const printRow = (label, value, xLabel, xValue, y, maxWidth = 55) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, xLabel, y);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(String(value || ''), maxWidth);
      doc.text(lines, xValue, y);
      return lines.length;
    };

    // Header
    doc.addImage(logo, "PNG", 14, 14, 30, 6); // Adjust dimensions as needed
    doc.setFontSize(10);
    doc.text("INTERPACK S.A.S.", 50, 15);
    doc.setFontSize(8);
    doc.text(`Fecha de Generación: ${new Date().toLocaleString()}`, 130, 15);
    doc.text("Dirección: Santa Lucía - Ecuador", 50, 20); // Placeholder
    doc.text("Teléfono: 0980169890", 50, 25); // Placeholder
    doc.setFontSize(6);
    doc.text("Este comprobante no posee efectos tributarios y tiene como objetivo", 130, 20)
    doc.text("ser un respaldo exclusivamente informativo.", 130, 22);

    doc.line(14, 30, 196, 30);

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("COMPROBANTE DE RECEPCIÓN DE PRODUCTO #" + proceso.secuencial, 105, 40, { align: "center" });
    doc.setFont("helvetica", "normal");

    let yPos = 55;
    doc.setFontSize(10);

    // Info block
    if (isGranel) {
      let l1 = printRow("Frutal al granel:", "Sí", 14, 50, yPos);
      let l2 = printRow("Secuencial:", proceso.secuencial, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Productor:", proceso.productorNombre, 14, 50, yPos);
      l2 = printRow("Producto:", proceso.producto, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Conductor:", proceso.nombreConductor, 14, 50, yPos);
      l2 = printRow("Cédula:", proceso.cedulaConductor, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Fecha Recepción:", proceso.fechaInicio, 14, 50, yPos);
      l2 = printRow("Vehículo:", proceso.vehiculo, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Placa:", proceso.placa, 14, 50, yPos);
      l2 = printRow("Guía de Remisión:", proceso.guiaRemision, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Precinto:", proceso.precinto, 14, 50, yPos);
      l2 = printRow("Gavetas/Cajas:", totalProcesadasGranel, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Observaciones:", proceso.observaciones || '', 14, 50, yPos);

      yPos += l1 * 5 + 7;

      // Table Granel
      const columns = [
        { header: "Tara Individual", dataKey: "taraIndividual" },
        { header: "Procesadas (Gavetas)", dataKey: "procesadas" },
        { header: "Tara (kg)", dataKey: "tara" },
        { header: "Peso Bruto", dataKey: "pesoBrutoTotal" },
        { header: "Peso Ingreso", dataKey: "pesoIngreso" },
        { header: "Observaciones", dataKey: "observaciones" }
      ];

      const data = ingresosGranel.map(item => ({
        taraIndividual: item.taraIndividual,
        procesadas: item.procesadas,
        tara: (item.taraIndividual * item.procesadas).toFixed(2),
        pesoBrutoTotal: item.pesoBrutoTotal,
        pesoIngreso: (item.pesoBrutoTotal - ((item.taraIndividual * item.procesadas)).toFixed(2)),
        observaciones: item.observaciones
      }));

      // Footer row
      const footerRow = [{
        taraIndividual: "TOTAL",
        procesadas: totalProcesadasGranel,
        tara: totalTaraGranel.toFixed(2),
        pesoBrutoTotal: totalPesoBrutoGranel.toFixed(2),
        pesoIngreso: totalPesoIngresoGranel.toFixed(2),
        observaciones: ""
      }];

      autoTable(doc, {
        head: [columns.map(c => c.header)],
        body: data.map(r => Object.values(r)),
        foot: footerRow.map(r => Object.values(r)),
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 8,
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          halign: 'center',
          valign: 'middle'
        },
        columnStyles: {
          0: { halign: 'center' },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'center' },
          4: { halign: 'center' },
          5: { halign: 'center' },
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        footStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        }
      });

    } else {
      // Cajas Logic
      let l1 = printRow("Frutal al granel:", "No", 14, 50, yPos);
      let l2 = printRow("Secuencial:", proceso.secuencial, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Productor:", proceso.productorNombre, 14, 50, yPos);
      l2 = printRow("Producto:", proceso.producto, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Conductor:", proceso.nombreConductor, 14, 50, yPos);
      l2 = printRow("Cédula:", proceso.cedulaConductor, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Fecha Recepción:", proceso.fechaInicio, 14, 50, yPos);
      l2 = printRow("Vehículo:", proceso.vehiculo, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Placa:", proceso.placa, 14, 50, yPos);
      l2 = printRow("Guía de Remisión:", proceso.guiaRemision, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Precinto:", proceso.precinto, 14, 50, yPos);
      l2 = printRow("Gavetas/Cajas:", totalProcesadasCajas, 110, 145, yPos);
      yPos += Math.max(l1, l2) * 5 + 2;
      l1 = printRow("Observaciones:", proceso.observaciones || '', 14, 50, yPos);

      yPos += l1 * 5 + 7;

      const columns = [
        { header: "Peso Neto", dataKey: "pesoNeto" },
        { header: "Procesadas", dataKey: "procesadas" },
        { header: "Descarte", dataKey: "descarte" },
        { header: "Recibidas", dataKey: "recibidas" },
        { header: "Peso Ingreso", dataKey: "pesoIngreso" },
        { header: "Observaciones", dataKey: "observaciones" }
      ];
      const data = ingresosCajas.map(item => {
        const recibidas = item.procesadas - (item.descarte || 0);
        return {
          pesoNeto: item.pesoNeto,
          procesadas: item.procesadas,
          descarte: item.descarte,
          recibidas: recibidas,
          pesoIngreso: (item.pesoNeto * recibidas).toFixed(2)
        };
      });

      // Footer row
      const footerRow = [{
        pesoNeto: "TOTAL",
        procesadas: totalProcesadasCajas,
        descarte: totalDescarteCajas.toFixed(2),
        recibidas: totalRecibidasCajas,
        pesoIngreso: totalPesoIngresoCajas.toFixed(2)
      }];

      autoTable(doc, {
        head: [columns.map(c => c.header)],
        body: data.map(r => Object.values(r)),
        foot: footerRow.map(r => Object.values(r)),
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 8,
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        footStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        }
      });
    }

    // Signatures
    const finalY = (doc.lastAutoTable?.finalY || yPos) + 40;
    doc.line(30, finalY, 90, finalY);
    doc.text("Entregue Conforme", 40, finalY + 5);

    doc.line(120, finalY, 180, finalY);
    doc.text("Recibí Conforme", 135, finalY + 5);

    const uri = doc.output('datauristring');
    const base64 = uri.split('base64,')[1];
    setPreviewPdfUri(uri);
    setPreviewPdfBase64(base64);
    setPreviewTitle("Vista Previa del Comprobante de Recepción");
    setPreviewFilename(`Comprobante_Recepcion_${proceso.secuencial}.pdf`);
    setPreviewReportName("Comprobante de Recepción");
    setPreviewModalVisible(true);
  };

  const res_kgIngresados = totalPesoIngresoGranel + totalPesoIngresoCajas;
  const res_totalKgEgresados = totalKgEgresos;
  const res_kgRechazo1 = parseFloat(proceso.kgRechazo || 0);
  const res_kgRechazo2 = res_kgIngresados - res_kgRechazo1 - res_totalKgEgresados;
  const res_totalKgRechazados = res_kgRechazo1 + res_kgRechazo2;
  const res_porcentajeRechazo = res_kgIngresados > 0 ? (res_totalKgRechazados / res_kgIngresados * 100) : 0;

  const handleSendPdf = async () => {
    setSendingPdf(true);
    try {
      await api.post(`/proceso/enviar-informe/${id}`, {
        pdfBase64: previewPdfBase64,
        nombreInforme: previewReportName
      });
      setPreviewModalVisible(false);
      navigate("/informe-resultado", {
        state: {
          status: "success",
          message: "¡El informe se envió exitosamente!",
          subTitle: `El PDF relacionado al proceso ${proceso?.secuencial} fue enviado y registrado.`,
          returnUrl: `/procesos/${id}`
        }
      });
    } catch (err) {
      setPreviewModalVisible(false);
      const errorMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : "Error al enviar el PDF";
      navigate("/informe-resultado", {
        state: {
          status: "error",
          message: "Error en el envío",
          subTitle: errorMsg,
          returnUrl: `/procesos/${id}`
        }
      });
    } finally {
      setSendingPdf(false);
    }
  };

  const handleDownloadPdf = () => {
    const link = document.createElement("a");
    link.href = previewPdfUri;
    link.download = previewFilename;
    link.click();
  };

  const exportarDescarte = async () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.addImage(logo, "PNG", 14, 14, 30, 6);
      doc.setFontSize(10);
      doc.text("INTERPACK S.A.S.", 50, 15);
      doc.setFontSize(8);
      doc.text(`Fecha de Generación: ${new Date().toLocaleString()}`, 130, 15);
      doc.text("Dirección: Santa Lucía - Ecuador", 50, 20);
      doc.text("Teléfono: 0980169890", 50, 25);
      doc.setFontSize(6);
      doc.text("Este comprobante no posee efectos tributarios y tiene como objetivo", 130, 20);
      doc.text("ser un respaldo exclusivamente informativo.", 130, 22);
      doc.line(14, 30, 196, 30);

      // Title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("REPORTE DE DESCARTE Y EVIDENCIA FOTOGRÁFICA #" + proceso.secuencial, 105, 40, { align: "center" });
      doc.setFont("helvetica", "normal");

      let yPos = 48;

      // Fix: wrap condition in parentheses to avoid operator precedence bug
      const exportadorInfo = (proceso.exportadorNombre || proceso.exportadorIdentificacion)
        ? `${proceso.exportadorNombre || 'N/A'} (${proceso.exportadorIdentificacion || 'N/A'})`
        : 'N/A';

      const dataInfo = [
        [
          { content: "Productor:", styles: { fontStyle: 'bold' } },
          `${proceso.productorNombre} (${proceso.productorIdentificacion})`,
          { content: "Jornada:", styles: { fontStyle: 'bold' } },
          proceso.jornada || "-"
        ],
        [
          { content: "Tipo de Producción:", styles: { fontStyle: 'bold' } },
          proceso.tipoProduccion || "-",
          { content: "Producto:", styles: { fontStyle: 'bold' } },
          proceso.producto || "-"
        ],
        [
          { content: "Conductor:", styles: { fontStyle: 'bold' } },
          `${proceso.nombreConductor || "-"} (${proceso.cedulaConductor || "-"})`,
          { content: "Vehículo / Placa:", styles: { fontStyle: 'bold' } },
          `${proceso.vehiculo || "-"} / ${proceso.placa || "-"}`
        ],
        [
          { content: "Exportador:", styles: { fontStyle: 'bold' } },
          exportadorInfo,
          { content: "Guía de Remisión:", styles: { fontStyle: 'bold' } },
          proceso.guiaRemision || "-"
        ],
        [
          { content: "Precinto:", styles: { fontStyle: 'bold' } },
          proceso.precinto || "-",
          { content: "Estado:", styles: { fontStyle: 'bold' } },
          proceso.estado || "-"
        ],
        [
          { content: "Kg Rechazo:", styles: { fontStyle: 'bold' } },
          String(proceso.kgRechazo ?? "0"),
          { content: "Fecha Inicio:", styles: { fontStyle: 'bold' } },
          proceso.fechaInicio || "-"
        ],
        [
          { content: "Fecha Fin:", styles: { fontStyle: 'bold' } },
          proceso.fechaFin || "En proceso",
          { content: "Usuario Generó:", styles: { fontStyle: 'bold' } },
          proceso.usuarioGeneroNombre || "-"
        ],
        [
          { content: "Usuario Finalizó:", styles: { fontStyle: 'bold' } },
          proceso.usuarioFinalizoNombre || "-",
          { content: "", styles: { fontStyle: 'bold' } },
          ""
        ],
        [
          { content: "Observaciones:", styles: { fontStyle: 'bold' } },
          { content: proceso.observaciones || "-", colSpan: 3 }
        ]
      ];

      autoTable(doc, {
        body: dataInfo,
        startY: yPos,
        theme: 'grid',
        styles: {
          fontSize: 9,
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          valign: 'middle'
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 55 },
          2: { cellWidth: 35 },
          3: { cellWidth: 55 }
        },
        alternateRowStyles: { fillColor: [255, 255, 255] }
      });

      const pageHeight = doc.internal.pageSize.height;
      const margin = 14;
      const imgWidth = 80;
      const imgHeight = 60;
      const gap = 10;

      yPos = doc.lastAutoTable.finalY + 15;

      if (yPos + 15 > pageHeight - margin) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("REGISTRO FOTOGRÁFICO DE DESCARTE", 14, yPos);
      yPos += 10;

      if (imagenes.length === 0) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        doc.text("No se encontraron evidencias fotográficas para este proceso.", 14, yPos);
      } else {
        // Pre-load images via HTMLImageElement (required for jsPDF v4+)
        const loadImage = (base64Str) =>
          new Promise((resolve) => {
            const imgEl = new window.Image();
            imgEl.onload = () => resolve(imgEl);
            imgEl.onerror = () => resolve(null);
            imgEl.src = `data:image/jpeg;base64,${base64Str}`;
          });

        for (let index = 0; index < imagenes.length; index++) {
          const img = imagenes[index];
          const col = index % 2;
          const x = margin + col * (imgWidth + gap);
          const requiredHeight = 8 + imgHeight + gap;

          if (col === 0 && yPos + requiredHeight > pageHeight - margin) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(`Evidencia ${index + 1}`, x, yPos);

          const imgEl = await loadImage(img.base64);
          if (imgEl) {
            try {
              doc.addImage(imgEl, "JPEG", x, yPos + 3, imgWidth, imgHeight);
            } catch (imgErr) {
              console.error(`Error al insertar imagen ${index + 1}:`, imgErr);
            }
          }

          if (col === 1 || index === imagenes.length - 1) {
            yPos += imgHeight + gap + 8;
          }
        }
      }

      doc.save(`Reporte_Descarte_${proceso.secuencial}.pdf`);
    } catch (err) {
      console.error("Error generando el reporte de descarte:", err);
      message.error("Error al generar el reporte de descarte.");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: "16px" }}>
        <Title level={3} style={{ margin: 0 }}>Detalle del Proceso {proceso.secuencial}</Title>
        <div style={{ display: "flex", gap: "8px" }}>
          {(userRole === "Super Admin" || userRole === "Administrador") && (
            <Button type="default" onClick={() => setEditProcesoVisible(true)}>
              Editar Datos
            </Button>
          )}
          {userRole === "Super Admin" && (
            <Button type="primary" onClick={handleOpenModal}>
              Cambiar Estado
            </Button>
          )}
        </div>
      </div>

      {/* Información general */}
      <Card style={{ marginBottom: 24 }}>
        <Descriptions className="fixed-layout-descriptions" column={{ xs: 1, sm: 2, md: 2 }} layout={!screens.md ? "vertical" : "horizontal"} bordered>
          <Descriptions.Item label="Productor">
            {proceso.productorNombre} ({proceso.productorIdentificacion})
          </Descriptions.Item>
          <Descriptions.Item label="Jornada">{proceso.jornada}</Descriptions.Item>
          <Descriptions.Item label="Tipo de Producción">{proceso.tipoProduccion}</Descriptions.Item>
          <Descriptions.Item label="Producto">{proceso.producto}</Descriptions.Item>

          <Descriptions.Item label="Conductor">
            {proceso.nombreConductor} ({proceso.cedulaConductor})
          </Descriptions.Item>
          <Descriptions.Item label="Vehículo / Placa">
            {proceso.vehiculo} / {proceso.placa}
          </Descriptions.Item>
          <Descriptions.Item label="Exportador">
            {proceso.exportadorNombre || proceso.exportadorIdentificacion
              ? `${proceso.exportadorNombre || 'N/A'} (${proceso.exportadorIdentificacion || 'N/A'})`
              : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Guía de Remisión">{proceso.guiaRemision}</Descriptions.Item>
          <Descriptions.Item label="Precinto">{proceso.precinto}</Descriptions.Item>

          <Descriptions.Item label="Estado">{proceso.estado}</Descriptions.Item>
          <Descriptions.Item label="Kg Rechazo">{proceso.kgRechazo}</Descriptions.Item>

          <Descriptions.Item label="Fecha Inicio">{proceso.fechaInicio}</Descriptions.Item>
          <Descriptions.Item label="Fecha Fin">{proceso.fechaFin || "En proceso"}</Descriptions.Item>

          <Descriptions.Item label="Usuario Generó">{proceso.usuarioGeneroNombre}</Descriptions.Item>
          <Descriptions.Item label="Usuario Finalizó">{proceso.usuarioFinalizoNombre || "-"}</Descriptions.Item>

          <Descriptions.Item label="Observaciones" span={2}>
            {proceso.observaciones}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Ingresos Granel */}
      {ingresosGranel.length > 0 && (
        <Card title="Ingresos - Granel" style={{ marginBottom: 24 }}>
          <Table
            dataSource={ingresosGranel}
            rowKey="idingresos"
            columns={columnsIngresosGranel}
            pagination={false}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}><strong>{totalProcesadasGranel}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}><strong>{totalPesoBrutoGranel.toFixed(2)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}><strong>{totalTaraGranel.toFixed(2)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}><strong>{totalPesoIngresoGranel.toFixed(2)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}

      {/* Ingresos Cajas */}
      {ingresosCajas.length > 0 && (
        <Card title="Ingresos - Cajas" style={{ marginBottom: 24 }}>
          <Table
            dataSource={ingresosCajas}
            rowKey="idingresosCajas"
            columns={columnsIngresosCajas}
            pagination={false}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}><strong>{totalProcesadasCajas}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}><strong>{totalDescarteCajas.toFixed(2)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}><strong>{totalRecibidasCajas}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={4}><strong>{totalPesoIngresoCajas.toFixed(2)}</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}

      {/* Egresos */}
      <Card title="Egresos" style={{ marginBottom: 24 }}>
        <Table
          dataSource={egresos}
          rowKey="idegresos"
          columns={columnsEgresos}
          pagination={false}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}><strong>{totalCajasEgresos}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3}><strong>{totalKgEgresos.toFixed(2)}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Resumen Final */}
      <Card title="Resumen del Proceso" style={{ marginBottom: 24 }}>
        <Table
          dataSource={[
            {
              kgIngresados: res_kgIngresados.toFixed(2),
              kgRechazo1: res_kgRechazo1.toFixed(2),
              kgRechazo2: res_kgRechazo2.toFixed(2),
              totalKgRechazados: res_totalKgRechazados.toFixed(2),
              porcentajeRechazo: res_porcentajeRechazo.toFixed(2),
              totalKgEgresados: res_totalKgEgresados.toFixed(2)
            }
          ]}
          rowKey="kgIngresados"
          pagination={false}
          columns={[
            { title: "Kg Ingresados", dataIndex: "kgIngresados" },
            { title: "Kg Rechazo 1", dataIndex: "kgRechazo1" },
            { title: "Kg Rechazo 2", dataIndex: "kgRechazo2" },
            { title: "Total Kg Rechazados", dataIndex: "totalKgRechazados" },
            { title: "% Rechazo", dataIndex: "porcentajeRechazo" },
            { title: "Total Kg Procesados", dataIndex: "totalKgEgresados" }
          ]}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Imagenes */}
      {imagenes.length > 0 && (
        <Card title="Evidencia Fotográfica" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {imagenes.map((img, idx) => (
              <div key={idx} style={{ position: "relative", display: "inline-block" }}>
                <Image
                  width={200}
                  src={`data:image/jpeg;base64,${img.base64}`}
                  alt={`Evidencia ${idx + 1}`}
                />
                <Popconfirm
                  title="¿Estás seguro de eliminar esta imagen?"
                  onConfirm={() => handleDeleteImage(img.idImagen)}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
                  />
                </Popconfirm>
              </div>
            ))}
          </div>
        </Card>
      )}



      <div style={{ display: "flex", gap: "10px", marginBottom: 50, flexWrap: "wrap" }}>
        <Button type="primary" onClick={exportarComprobante}>
          Previsualizar Comprobante de Recepción
        </Button>
        <Button type="default" onClick={exportarInforme}>
          Previsualizar Informe de Proceso
        </Button>
        <Button type="primary" danger onClick={exportarDescarte}>
          Previsualizar Reporte de Descarte
        </Button>
        {proceso.informesEnviados && proceso.informesEnviados.length > 0 && (
          <Button onClick={() => setHistoryModalVisible(true)}>
            Ver Historial de Envíos
          </Button>
        )}
      </div>

      {/* Modal for status change */}
      <Modal
        title="Cambiar Estado del Proceso"
        open={isModalVisible}
        onOk={handleUpdateStatus}
        onCancel={handleCloseModal}
        confirmLoading={updatingStatus}
        okText="Actualizar"
        cancelText="Cancelar"
      >
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Estado actual: <span style={{ fontWeight: "normal" }}>{proceso?.estado}</span>
          </label>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Nuevo estado:
          </label>
          <Select
            value={newStatus}
            onChange={setNewStatus}
            style={{ width: "100%" }}
            placeholder="Seleccione un estado"
          >
            <Select.Option value="Realizando Ingreso">Realizando Ingreso</Select.Option>
            <Select.Option value="Realizando Egreso">Realizando Egreso</Select.Option>
            <Select.Option value="Finalizado">Finalizado</Select.Option>
          </Select>
        </div>
      </Modal>

      {/* Modal PDF Preview */}
      <Modal
        title={previewTitle}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPreviewModalVisible(false)}>
            Cerrar
          </Button>,
          <Button key="download" onClick={handleDownloadPdf}>
            Descargar
          </Button>,
          <Button key="send" type="primary" loading={sendingPdf} onClick={handleSendPdf}>
            Enviar
          </Button>,
        ]}
        width={800}
      >
        {previewPdfUri ? (
          <iframe
            title="PDF Preview"
            src={previewPdfUri}
            style={{ width: "100%", height: "60vh", border: "none" }}
          />
        ) : (
          <p>Generando vista previa...</p>
        )}
      </Modal>

      {/* Modal Historial de Informes */}
      <Modal
        title="Historial de Informes Enviados"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Cerrar
          </Button>
        ]}
      >
        <Table
          dataSource={proceso.informesEnviados}
          rowKey="idInformeHistorial"
          pagination={false}
          columns={[
            {
              title: "Fecha de Envío",
              dataIndex: "fechaEnvio",
              render: (val) => new Date(val).toLocaleString()
            },
            {
              title: "Nombre del Informe",
              dataIndex: "nombreInforme"
            }
          ]}
        />
      </Modal>

      {/* Edit Modals */}
      <EditIngresoCajaModal
        open={editIngresoCajaVisible}
        record={selectedIngresoCaja}
        onCancel={() => {
          setEditIngresoCajaVisible(false);
          setSelectedIngresoCaja(null);
        }}
        onSuccess={() => {
          setEditIngresoCajaVisible(false);
          setSelectedIngresoCaja(null);
          fetchProceso();
        }}
      />

      <EditIngresoGranelModal
        open={editIngresoGranelVisible}
        record={selectedIngresoGranel}
        onCancel={() => {
          setEditIngresoGranelVisible(false);
          setSelectedIngresoGranel(null);
        }}
        onSuccess={() => {
          setEditIngresoGranelVisible(false);
          setSelectedIngresoGranel(null);
          fetchProceso();
        }}
      />

      <EditEgresoModal
        open={editEgresoVisible}
        record={selectedEgreso}
        onCancel={() => {
          setEditEgresoVisible(false);
          setSelectedEgreso(null);
        }}
        onSuccess={() => {
          setEditEgresoVisible(false);
          setSelectedEgreso(null);
          fetchProceso();
        }}
      />

      <EditProcesoModal
        open={editProcesoVisible}
        record={proceso}
        onCancel={() => setEditProcesoVisible(false)}
        onSuccess={() => {
          setEditProcesoVisible(false);
          fetchProceso();
        }}
      />
    </div>
  );
};

export default DetalleProcesoPage;

